import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from 'components/common/layout/Layout';
import SearchFilter from 'components/common/forms/SearchFilter';
import Paging from 'components/common/nav/Paging';
import classNames from 'classnames';
import styles from 'styles/pages/professor/RegisteredRound.module.scss';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { DEFAULT_PAGE_SIZE } from 'constants/page';
import useModal from 'hooks/useModal';
import {
  SortDirection,
  TRegisteredRoundListParams,
} from 'types/apis/professor';
import { useQuery } from 'react-query';
import { getRegisteredRoundList } from 'apis/professor';
import RegisteredRoundTable from './RegisteredRoundTable';
import {
  RegisteredRoundSortingLabel,
  RegisteredRoundSortType,
} from 'types/form/professor';
import RegisterRoundModal from 'pages/modal/RegisterRoundModal';

const RegisteredRound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const getParamsFromQuery = () => {
    return {
      courseInfoId: Number(query.get('courseInfoId')) || undefined,
      searchWord: query.get('searchWord') || undefined,
      isClosed: (activeTabIndex === 1 && true) || false,
      sortBy: (query.get('sortBy') as RegisteredRoundSortType) || undefined,
      isAsc:
        query.get('sort') === 'asc'
          ? true
          : query.get('sort') === 'desc'
          ? false
          : undefined,
    };
  };

  const { openModal } = useModal();

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [page, setPage] = useState(Number(query.get('page')) || 1);
  const [sortBy, setSortBy] = useState(query.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(query.get('sort') || '');
  const [params, setParams] = useState<TRegisteredRoundListParams>({
    page,
    size: DEFAULT_PAGE_SIZE,
    ...getParamsFromQuery(),
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!query.has('isClosed')) {
      query.set('isClosed', 'false');
      navigate({
        search: query.toString(),
      });
    }
  }, []);

  useEffect(() => {
    setParams({
      ...params,
      page,
      ...getParamsFromQuery(),
    });
  }, [location.search, page, activeTabIndex]);

  const {
    data: getRegisteredRoundListData,
    refetch: getRegisteredRoundListRefetch,
    isFetching,
  } = useQuery(['getRegisteredRoundList', params], () =>
    getRegisteredRoundList(params)
  );

  const tableData = getRegisteredRoundListData?.data?.registeredChartList || [];
  const tablePaging = getRegisteredRoundListData?.data?.paging;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickRegister = () =>
    openModal({
      element: <RegisterRoundModal />,
    });

  const onClickSort = (label: string) => {
    const sortAsc =
      sortDirection === ''
        ? SortDirection.Asc
        : sortDirection === SortDirection.Asc
        ? SortDirection.Desc
        : '';

    const sortBy = {
      [RegisteredRoundSortingLabel.ChartRoundAndChartName]:
        RegisteredRoundSortType.ChartRoundAndChartName,
      [RegisteredRoundSortingLabel.FirstSubmitEndDate]:
        RegisteredRoundSortType.FirstSubmitEndDate,
      [RegisteredRoundSortingLabel.SecondSubmitEndDate]:
        RegisteredRoundSortType.SecondSubmitEndDate,
    }[label as RegisteredRoundSortingLabel];

    setSortDirection(sortAsc);
    setSortBy(sortBy);

    if (page !== 1) {
      setPage(1);
    }
    if (!sortAsc) {
      query.delete('sort');
      query.delete('sortBy');
    } else {
      query.set('sort', sortAsc);
      query.set('sortBy', sortBy);
    }
    navigate({
      search: query.toString(),
    });

    setParams({
      ...params,
      page: 1,
      sortBy: (query.get('sortBy') as RegisteredRoundSortType) || undefined,
      isAsc:
        sortAsc === SortDirection.Asc
          ? true
          : sortAsc === SortDirection.Desc
          ? false
          : undefined,
      isClosed: (activeTabIndex === 1 && true) || false,
    });
  };

  const onClickRoundTab = () => {
    if (activeTabIndex === 0) {
      setActiveTabIndex(1);
    } else {
      setActiveTabIndex(0);
    }

    if (page !== 1) {
      setPage(1);
    } else {
      if (activeTabIndex === 1) {
        query.set('isClosed', 'false');
      } else {
        query.set('isClosed', 'true');
      }
      navigate({
        search: query.toString(),
      });
    }

    setParams({
      ...params,
      page: 1,
      isClosed: (activeTabIndex === 1 && true) || false,
    });
  };

  useEffect(() => {
    setParams({
      ...params,
      page,
    });
  }, [page]);

  return (
    <>
      <Layout>
        <div className="flex justify_between items_center mt40">
          <div className="flex gap24">
            <div
              className={classNames(styles.tab, {
                [styles.tab_active]: activeTabIndex === 0,
              })}
              onClick={onClickRoundTab}
            >
              <span>등록 회차</span>
            </div>
            <div
              className={classNames(styles.tab, {
                [styles.tab_active]: activeTabIndex === 1,
              })}
              onClick={onClickRoundTab}
            >
              <span>마감 회차</span>
            </div>
          </div>
        </div>

        <SearchFilter
          dropdown={[
            {
              id: 'course',
              placeholder: '강의명',
              width: '204px',
            },
            {
              id: 'courseInfo',
              placeholder: '분반',
              width: '188px',
            },
          ]}
          input={{
            placeholder: '(회차)회차명을 입력해 주세요.',
            width: '333px',
          }}
          onChange={() => setPage(1)}
        >
          <button
            type="button"
            className={styles.btn_wrapper}
            onClick={onClickRegister}
          >
            <PlusIcon fill={STYLES_VARIABLES.PURPLE_01} />
            <span className={styles.btn}>실습 회차 등록</span>
          </button>
        </SearchFilter>

        <div className={styles.text_wrapper}>
          <span className={styles.text}>
            총 <span>{tablePaging?.totalElements || 0}</span>개
          </span>
        </div>

        <RegisteredRoundTable
          activeTabIndex={activeTabIndex}
          data={tableData}
          paging={tablePaging || { number: 0, totalPages: 0, totalElements: 0 }}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onClickSort={onClickSort}
          refetchRound={getRegisteredRoundListRefetch}
          isLoading={isLoading}
        />

        {/* 페이지네이션 */}
        {activeTabIndex === 0 &&
          tablePaging &&
          tablePaging.totalElements > 0 && (
            <div className="mt32">
              <Paging
                activePage={page}
                totalItemsCount={tablePaging.totalElements}
                handlePageChange={setPage}
              />
            </div>
          )}

        {activeTabIndex === 1 &&
          tablePaging &&
          tablePaging.totalElements > 0 && (
            <div className="mt32">
              <Paging
                activePage={page}
                totalItemsCount={tablePaging.totalElements}
                handlePageChange={setPage}
              />
            </div>
          )}
      </Layout>
    </>
  );
};
export default RegisteredRound;
